import { BitkitButton, IconMail, useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { useEffect } from 'react';

import { SocialLoginProviders } from '@/constants';
import useQueryParams from '@/hooks/useQueryParams';
import { updateQueryString } from '@/utils/queryParams';
import { getCsrfTokenCookie } from '@/utils/tokens';

import HorizontalRulerWithContent from '../../HorizontalRulerWithContent/HorizontalRulerWithContent';
import SocialLoginButton from '../SocialLoginButton/SocialLoginButton';

type LoginButtonGroupProps = {
  signUpMode: boolean;
  onEmailSelect?: () => void;
  ssoButtonProps: { onClick?: () => void; as?: 'a'; href?: string };
  rememberMe?: boolean;
  onTransparentBackground?: boolean;
};

const gitProviders: SocialLoginProviders[] = [
  SocialLoginProviders.github,
  SocialLoginProviders.bitbucket,
  SocialLoginProviders.gitlab,
];

const LoginButtonGroup = ({
  signUpMode,
  onEmailSelect = () => {},
  ssoButtonProps,
  rememberMe,
  onTransparentBackground = true,
}: LoginButtonGroupProps) => {
  const token = getCsrfTokenCookie();
  const params = useQueryParams();
  const useSocialLogin = params.get('use');
  const rememberMePart = typeof rememberMe === 'boolean' ? `?remember_me=${rememberMe}` : '';
  const { isMobile } = useResponsive();

  useEffect(() => {
    setTimeout(() => {
      if (!useSocialLogin) {
        return;
      }

      switch (useSocialLogin.toLowerCase()) {
        case 'sso':
          if (ssoButtonProps.onClick) {
            params.delete('use');
            updateQueryString(params, 'replace');
            ssoButtonProps.onClick();
          } else {
            window.location.assign('/users/sign-in/saml');
          }

          return;
        case 'github':
        case 'gitlab':
        case 'bitbucket':
        case 'google':
          document.forms.namedItem(`login_with_${useSocialLogin}`)?.submit();
      }
    }, 300);
  }, [useSocialLogin, params, ssoButtonProps]);

  const beforeText = {
    default: signUpMode ? 'Sign up with' : 'Continue with',
    github: signUpMode ? 'Configure' : 'Continue with',
  };

  const afterText = {
    default: '',
    github: signUpMode ? 'access' : '',
  };

  const renderGitProviderButtons = () => {
    return gitProviders.map((provider) => (
      <chakra.form
        name={`login_with_${provider.toLowerCase()}`}
        key={provider}
        action={`/users/auth/${provider}${rememberMePart}`}
        method="post"
        flexGrow={1}
        width="100%"
      >
        <input type="hidden" name="authenticity_token" value={token} />
        <SocialLoginButton
          type="submit"
          key={provider}
          provider={provider}
          textBefore={beforeText.default}
          textAfter={afterText.default}
          width="100%"
          size={isMobile ? 'md' : 'lg'}
        />
      </chakra.form>
    ));
  };

  const renderRegularLoginButtons = () => (
    <>
      <BitkitButton
        width="100%"
        variant="secondary"
        icon={IconMail}
        size={isMobile ? 'md' : 'lg'}
        onClick={onEmailSelect}
      >
        {`${beforeText.default} Email ${afterText.default}`.trim()}
      </BitkitButton>

      <SocialLoginButton
        name="login_with_sso"
        provider={SocialLoginProviders.saml}
        {...ssoButtonProps}
        textBefore={beforeText.default}
        width="100%"
        size={isMobile ? 'md' : 'lg'}
        _hover={{ backgroundColor: 'color/neutral/subtle' }}
      />

      <chakra.form
        name="login_with_google"
        action={`/users/auth/google${rememberMePart}`}
        method="post"
        flexGrow={1}
        width="100%"
      >
        <input type="hidden" name="authenticity_token" value={token} />
        <SocialLoginButton
          type="submit"
          provider={SocialLoginProviders.google}
          textBefore={beforeText.default}
          width="100%"
          size={isMobile ? 'md' : 'lg'}
          _hover={{ backgroundColor: 'color/neutral/subtle' }}
        />
      </chakra.form>
    </>
  );

  return (
    <Box display="flex" flexDirection="column" gap={['12', '16']}>
      {renderGitProviderButtons()}

      <HorizontalRulerWithContent backgroundColor={onTransparentBackground ? 'color/white/40' : 'border/minimal'}>
        <Text
          textStyle="body/md/semibold"
          marginX="8"
          color={onTransparentBackground ? 'sys/ui/header/control/indicator' : 'text/tertiary'}
          textTransform="uppercase"
        >
          or
        </Text>
      </HorizontalRulerWithContent>

      {renderRegularLoginButtons()}
    </Box>
  );
};

export default LoginButtonGroup;
